import { FEATURED_LINKS, LANDING_PAGE } from 'constants/ShopYourStore';
import Empty from 'constants/empty';
import { UPPER_FUNNEL_REFINEMENTS } from 'constants/UpperFunnel';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import urlUtils from 'utils/Url';
import localeUtils from 'utils/LanguageLocale';
import Location from 'utils/Location';
import basketConstants from 'constants/Basket';
import storeUtils from 'utils/Store';
import anaConsts from 'analytics/constants';

const { SHOP_STORE_AND_DELIVERY_FLYOUT } = LOCAL_STORAGE;
const { DELIVERY_OPTIONS } = basketConstants;

function excludeShopYourStoreMenuItems(menuItems = Empty.Array) {
    if (!menuItems.length) {
        return menuItems;
    }

    const excludedLinkSids = [FEATURED_LINKS.SHOP_SAME_DAY_SID, FEATURED_LINKS.SHOP_MY_STORE_SID];
    const filteredMenuItems = menuItems.filter(item => {
        const sid = item?.sid || '';

        return !excludedLinkSids.some(excludedSid => sid.startsWith(excludedSid));
    });

    return filteredMenuItems;
}

function transformCardRenderings(services = {}) {
    /*
    OMNX-534: Transform this structure:

    {
        "items": [{ "type": "CardDatasource" }, { "type": "CardDatasource" }],
        "parameters": {},
        "type": "CardRendering"
    }

    Into:

    [
        {
            "datasource": {"type": "CardDatasource"},
            "parameters": {},
            "type": "CardRendering"
        },
        {
            "datasource": {"type": "CardDatasource"},
            "parameters": {},
            "type": "CardRendering"
        }
    ]
    */

    const { items, parameters = {}, type } = services;

    if (!Array.isArray(items) || items.length === 0) {
        return null;
    }

    const result = items.map(item => {
        return {
            type,
            parameters,
            datasource: item
        };
    });

    return result;
}

function updateCachedSLAInformation(target, details) {
    try {
        const cache = Storage.session.getItem(SHOP_STORE_AND_DELIVERY_FLYOUT);

        if (cache) {
            let nextCache;

            if (target === LANDING_PAGE.STORE_DETAILS && cache?.storeDetails) {
                nextCache = {
                    ...cache,
                    storeDetails: {
                        ...cache.storeDetails,
                        pickupMessage: details?.pickupMessage
                    }
                };
            } else if (target === LANDING_PAGE.ZIP_CODE_DETAILS && cache?.sameDay) {
                nextCache = {
                    ...cache,
                    sameDay: {
                        ...cache.sameDay,
                        deliveryMessage: details?.deliveryMessage
                    }
                };
            }

            if (nextCache) {
                Storage.session.setItem(SHOP_STORE_AND_DELIVERY_FLYOUT, nextCache, Storage.MINUTES * 15);
            }
        }
    } catch (error) {
        Sephora.logger.error('Failed to update SLA information in cache:', error);
    }
}

function shopYourStoreEnabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'isShopYourStoreEnabled' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName] === true;

    return isEnabled;
}

function shopYourStoreV2Enabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'shopYourStoreV2' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName]?.isEnabled === true;

    return isEnabled;
}

function shopYourStoreNewBestEnabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'shopYourStoreNewAndBest' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName]?.isEnabled === true;

    return isEnabled;
}

function shopYourStoreFilterEnabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'shopYourStoreFilter' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName]?.isEnabled === true;

    return isEnabled;
}

function inventoryAwareStorePickerEnabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'inventoryAwareStorePicker' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName]?.isEnabled === true;

    return isEnabled;
}

function multiStoreFilteringEnabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'sysMultiStoreFiltering' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName]?.isEnabled === true;

    return isEnabled;
}

function persistRefinementsInUrl(url) {
    const isShopYourStoreV2Enabled = shopYourStoreV2Enabled();

    if (!url || !isShopYourStoreV2Enabled) {
        return url;
    }

    const PERSIST_REF_PARAMS = [...UPPER_FUNNEL_REFINEMENTS];
    const ref = urlUtils.getParams()?.ref || Empty.Array;

    const fulfillmentRefinements = ref.filter(r => {
        const key = r.split('=')[0];

        return PERSIST_REF_PARAMS.includes(key);
    });

    if (fulfillmentRefinements.length) {
        const refParam = fulfillmentRefinements.join(',');

        if (!url.includes('ref')) {
            const CONCAT_CHAR = !url.includes('?') ? '?' : '&';
            const urlWithRefinements = `${url}${CONCAT_CHAR}ref=${refParam}`;

            return urlWithRefinements;
        }
    }

    return url;
}

function getInitialDeliveryOption(killswitch) {
    const isShopYourStoreV2Enabled = killswitch ?? shopYourStoreV2Enabled();

    if (!isShopYourStoreV2Enabled) {
        return null;
    }

    if (Location.isShopMyStorePage()) {
        return DELIVERY_OPTIONS.PICKUP;
    } else if (Location.isShopSameDayPage()) {
        return DELIVERY_OPTIONS.SAME_DAY;
    }

    return null;
}

function mergeElegibilityFlags(items = [], eligibilityFlags = {}) {
    const itemsWithEligibilityFlags = [];

    for (const item of items) {
        const eligibility = eligibilityFlags[item?.productId] || Empty.Object;
        itemsWithEligibilityFlags.push({
            ...item,
            ...eligibility
        });
    }

    return itemsWithEligibilityFlags;
}

/**
 * Transforms the store/inventory API response into a structured pickupInventory format
 * @param {Object} params
 * @param {Array} params.stores - Array of store objects from the API
 * @param {Array} params.pickupItems - Array of pickup items with sku info
 * @param {Object} params.inventory - Inventory availability data
 * @param {string} params.currentSkuId - Optional SKU ID to get stock status for a specific product
 * @returns {Array} Transformed pickup inventory array containing storeId, storeType, displayName, itemCount, itemAvailableCount, items, and optional currentProduct
 */
function buildPickupInventory({ stores, pickupItems, inventory, currentSkuId }) {
    if (!stores || !inventory) {
        return [];
    }

    const availabilityByProducts = inventory.availabilityByProducts || [];

    return stores.map(store => {
        const items = (pickupItems || []).map(pickupItem => {
            const skuId = pickupItem.sku?.skuId || pickupItem.skuId;
            const skuImages = pickupItem.sku?.skuImages || {};
            const qty = pickupItem.qty || 1;

            // Find inventory data for this product
            const productInventory = availabilityByProducts.find(p => p.productId === skuId);

            // Find location data for this store
            const ftData = productInventory?.availabilityDetails?.availabilityByFT?.[0];
            const locationData = ftData?.locations?.find(loc => loc.location === store.storeId);

            const atp = locationData?.atp || 0;
            const outOfStock = atp === 0;
            const partialStock = atp > 0 && qty > atp;
            // availableQty is qty unless atp is less than qty
            const availableQty = Math.min(atp, qty);

            return {
                skuId,
                skuImages,
                outOfStock,
                partialStock,
                qty,
                availableQty
            };
        });

        // Compute totals
        const itemCount = items.reduce((sum, item) => sum + item.qty, 0);
        const itemAvailableCount = items.reduce((sum, item) => sum + item.availableQty, 0);

        const result = {
            storeId: store.storeId,
            storeType: store.storeType,
            displayName: store.displayName,
            itemCount,
            itemAvailableCount,
            items
        };

        // If currentSkuId is provided, add currentProduct stock information
        if (currentSkuId) {
            const currentProductInventory = availabilityByProducts.find(p => p.productId === currentSkuId);
            const ftData = currentProductInventory?.availabilityDetails?.availabilityByFT?.[0];
            const locationData = ftData?.locations?.find(loc => loc.location === store.storeId);
            const atp = locationData?.atp || 0;
            const outOfStock = atp === 0;

            result.currentProduct = {
                skuId: currentSkuId,
                outOfStock
            };
        }

        return result;
    });
}

/**
 * Gets the pickup inventory for a specific store
 * @param {Array} pickupInventory - Array of pickup inventory objects
 * @param {string} storeId - The store ID to find
 * @returns {Object|null} The store's pickup inventory or null if not found
 */
function getStorePickupInventory(pickupInventory, storeId) {
    if (!pickupInventory || !storeId || pickupInventory.length === 0) {
        return null;
    }

    return pickupInventory.find(inventory => inventory.storeId === storeId) || null;
}

/**
 * Builds a fake products string for analytics tracking based on pickup inventory
 * @param {Array} pickupInventory - Array of pickup inventory objects with store data, itemCount, itemAvailableCount
 * @returns {string} Analytics products string in the format:
 *   ;store-selector;;;event304|event307=X|event308=Y|event309=Z;eVar138=StoreName
 *   Uses event306 (no value) instead of event307 when itemAvailableCount is 0
 * @example
 *   // Returns: ";store-selector;;;event304|event307=2|event308=2|event309=4;eVar138=Embarcadero,;store-selector;;;event304|event306|event308=4|event309=4;eVar138=Union Street"
 *   buildFakeProductsString([
 *     { displayName: 'Embarcadero', itemCount: 4, itemAvailableCount: 2 },
 *     { displayName: 'Union Street', itemCount: 4, itemAvailableCount: 0 }
 *   ])
 */
function buildFakeProductsString(pickupInventory) {
    if (!pickupInventory || pickupInventory.length === 0) {
        return '';
    }

    const segments = pickupInventory.map(store => {
        const { itemCount, itemAvailableCount } = store;
        const unavailableCount = itemCount - itemAvailableCount;
        const displayName = storeUtils.getStoreDisplayName(store);

        // Build events part
        let eventsString = anaConsts.Event.EVENT_304;

        if (itemAvailableCount === 0) {
            eventsString += `|${anaConsts.Event.EVENT_306}`;
        } else {
            eventsString += `|${anaConsts.Event.EVENT_307}=${itemAvailableCount}`;
        }

        eventsString += `|${anaConsts.Event.EVENT_308}=${unavailableCount}|${anaConsts.Event.EVENT_309}=${itemCount}`;

        // Build complete segment
        return `;store-selector;;;${eventsString};eVar138=${displayName}`;
    });

    return segments.join(',');
}

/**
 * Builds a fake products string for store selection confirmation analytics
 * @param {Object} selectedStore - The selected store object with displayName
 * @returns {string} Analytics products string in the format:
 *   ;store-selector;;;event305;eVar138=StoreName
 * @example
 *   // Returns: ";store-selector;;;event305;eVar138=Embarcadero"
 *   buildFakeProductsStringForStoreSelection({ displayName: 'Embarcadero' })
 */
function buildFakeProductsStringForStoreSelection(selectedStore) {
    if (!selectedStore) {
        return '';
    }

    const displayName = storeUtils.getStoreDisplayName(selectedStore);

    return `;store-selector;;;${anaConsts.Event.EVENT_305};eVar138=${displayName}`;
}

/**
 * Builds a fake products string for PRODUCT variant analytics tracking
 * @param {string} productSku - The SKU of the current product
 * @param {Array} pickupInventory - Array of store objects with inventory data
 * @returns {string} Formatted products string with product SKU and store inventory data
 * @example
 *   // Returns: ";P12345;;;;eVar26=P12345,;product-store-selector;;;event304|event307=2|event308=2|event309=4|event312=1;eVar138=Embarcadero"
 *   buildFakeProductsStringForProductVariant('P12345', pickupInventory)
 */
function buildFakeProductsStringForProductVariant(productSku, pickupInventory) {
    if (!pickupInventory || pickupInventory.length === 0) {
        return '';
    }

    // First segment: product SKU
    const productSegment = `;${productSku};;;;eVar26=${productSku}`;

    // Store segments
    const storeSegments = pickupInventory.map(store => {
        const { itemCount, itemAvailableCount } = store;
        const unavailableCount = itemCount - itemAvailableCount;
        const displayName = storeUtils.getStoreDisplayName(store);

        // event312: Check if CURRENT PRODUCT is available at this store
        // 1 = in stock (outOfStock === false), 0 = out of stock (outOfStock === true)
        const isOutOfStock = store.currentProduct?.outOfStock ?? true; // Default to out of stock if missing
        const stockStatus = isOutOfStock ? '0' : '1';

        // Build events in numerical order: 304, 306/307, 308, 309, 312
        let eventsString = `${anaConsts.Event.EVENT_304}`;

        if (itemAvailableCount === 0) {
            eventsString += `|${anaConsts.Event.EVENT_306}`;
        } else {
            eventsString += `|${anaConsts.Event.EVENT_307}=${itemAvailableCount}`;
        }

        eventsString += `|${anaConsts.Event.EVENT_308}=${unavailableCount}|${anaConsts.Event.EVENT_309}=${itemCount}|${anaConsts.Event.EVENT_312}=${stockStatus}`;

        return `;product-store-selector;;;${eventsString};eVar138=${displayName}`;
    });

    return [productSegment, ...storeSegments].join(',');
}

/**
 * Builds a fake products string for PRODUCT variant store selection confirmation
 * @param {string} productSku - The SKU of the current product
 * @param {Object} selectedStore - The selected store object
 * @returns {string} Formatted products string with product SKU and store data
 * @example
 *   // Returns: ";P12345;;;;eVar26=P12345,;product-store-selector;;;event305;eVar138=Embarcadero"
 *   buildFakeProductsStringForStoreSelectionProductVariant('P12345', { displayName: 'Embarcadero' })
 */
function buildFakeProductsStringForStoreSelectionProductVariant(productSku, selectedStore) {
    if (!selectedStore) {
        return '';
    }

    const displayName = storeUtils.getStoreDisplayName(selectedStore);

    const productSegment = `;${productSku};;;;eVar26=${productSku}`;
    const storeSegment = `;product-store-selector;;;${anaConsts.Event.EVENT_305};eVar138=${displayName}`;

    return `${productSegment},${storeSegment}`;
}

/**
 * Extracts unique events from a fake products string
 * @param {string} fakeProductsString - The fake products string containing event data
 * @returns {Array<string>} Array of unique event names sorted numerically
 * @example
 *   // Returns: ["event304", "event306", "event308", "event309"]
 *   extractUniqueEvents(";store-selector;;;event304|event306|event308=1|event309=1;eVar138=Sephora AT KOHL'S MILPITAS,")
 */
function extractUniqueEvents(fakeProductsString) {
    if (!fakeProductsString) {
        return [];
    }

    const eventsSet = new Set();

    // Split by comma to handle multiple stores
    const segments = fakeProductsString.split(',');

    segments.forEach(segment => {
        // Split by semicolon to get all parts
        const parts = segment.split(';');

        // Find the part that contains events (starts with 'event' or contains '|event')
        const eventsPart = parts.find(part => {
            const trimmed = part.trim();

            return trimmed.startsWith('event') || trimmed.includes('|event');
        });

        if (eventsPart) {
            // Split by pipe to get individual events
            const events = eventsPart.split('|');

            events.forEach(event => {
                const trimmed = event.trim();

                // Only process if it starts with 'event'
                if (trimmed.startsWith('event')) {
                    // Remove the value part (everything after '=')
                    const eventName = trimmed.split('=')[0];

                    if (eventName) {
                        eventsSet.add(eventName);
                    }
                }
            });
        }
    });

    // Sort events numerically by extracting the number from eventXXX
    const sortedEvents = Array.from(eventsSet).sort((a, b) => {
        const numA = parseInt(a.replace('event', ''), 10);
        const numB = parseInt(b.replace('event', ''), 10);

        return numA - numB;
    });

    return sortedEvents;
}

/**
 * Builds a store list impression string for SoT sotV448 tracking
 * Format: <storeName>,<unavailableCount>,<availableCount>,<totalCount>;<storeName2>,...
 *
 * Supports two analytics use-cases:
 *  - **Store list impression** (omit `selectedStoreId`): all stores in `pickupInventory` are
 *    included, producing a full semicolon-delimited list fired when the store picker is displayed.
 *  - **CTA click** (provide `selectedStoreId`): only the matching store entry is included,
 *    producing a single-store string fired when the user confirms a store selection.
 *
 * @param {Array} pickupInventory - Array of store objects with inventory data
 * @param {string} [selectedStoreId] - Optional store ID used to scope the result to a single store.
 *   When provided, only the store whose `storeId` matches this value is included in the output
 *   (CTA click use-case). When omitted, all stores are included (impression use-case).
 * @returns {string} Semicolon-delimited store impression string, or an empty string if
 *   `pickupInventory` is empty or the `selectedStoreId` does not match any store
 * @example
 *   // Impression use-case — returns all stores:
 *   // "embarcadero,0,5,5;union street,1,4,5;powell,0,5,5"
 *   buildStoreListImpressionForSotV448([
 *     { displayName: 'embarcadero', itemCount: 5, itemAvailableCount: 5 },
 *     { displayName: 'union street', itemCount: 5, itemAvailableCount: 4 },
 *     { displayName: 'powell', itemCount: 5, itemAvailableCount: 5 }
 *   ])
 *
 * @example
 *   // CTA click use-case — returns only the selected store:
 *   // "union street,1,4,5"
 *   buildStoreListImpressionForSotV448([
 *     { storeId: '100', displayName: 'embarcadero', itemCount: 5, itemAvailableCount: 5 },
 *     { storeId: '200', displayName: 'union street', itemCount: 5, itemAvailableCount: 4 }
 *   ], '200')
 */
function buildStoreListImpressionForSotV448(pickupInventory, selectedStoreId) {
    // if selectedStoreId is provided, send only the selected store in sotV448 for the CTA click event, otherwise send all stores for the initial impression event
    if (!pickupInventory || pickupInventory.length === 0) {
        return '';
    }

    const filteredInventory = selectedStoreId ? pickupInventory.filter(store => store.storeId === selectedStoreId) : pickupInventory;
    const storeImpressions = filteredInventory.map(store => {
        const { itemCount, itemAvailableCount } = store;
        const unavailableCount = itemCount - itemAvailableCount;
        const displayName = storeUtils.getStoreDisplayName(store);

        // Format: storeName,unavailableCount,availableCount,totalCount
        return `${displayName},${unavailableCount},${itemAvailableCount},${itemCount}`;
    });

    return storeImpressions.join(';');
}

export default {
    excludeShopYourStoreMenuItems,
    transformCardRenderings,
    updateCachedSLAInformation,
    shopYourStoreNewBestEnabled,
    persistRefinementsInUrl,
    mergeElegibilityFlags,
    shopYourStoreEnabled,
    shopYourStoreV2Enabled,
    shopYourStoreFilterEnabled,
    inventoryAwareStorePickerEnabled,
    multiStoreFilteringEnabled,
    getInitialDeliveryOption,
    buildPickupInventory,
    getStorePickupInventory,
    buildFakeProductsString,
    buildFakeProductsStringForProductVariant,
    buildFakeProductsStringForStoreSelection,
    buildFakeProductsStringForStoreSelectionProductVariant,
    extractUniqueEvents,
    buildStoreListImpressionForSotV448
};
